import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@tanstack/react-router";
import { usePostHog } from "posthog-js/react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import {
  type FieldErrors,
  type UseFormReturn,
  useForm,
  useWatch,
} from "react-hook-form";
import { toast } from "sonner";
import { useSuspenseCapability } from "@/app/capabilities/api/hooks";
import { useOrgProjectRouteContext } from "@/lib/org-project-context/hooks";
import { useCreateMcpGateway, useUpdateMcpGateway } from "../api/hooks";
import {
  GATEWAY_DEFAULTS,
  gatewayToFormValues,
  getSelectedAppNames,
  INVALID_FIELD_LABELS,
  toGatewayPayload,
} from "../create-page/lib/form";
import {
  type GatewayFormState,
  getGatewayFormState,
  getSubmitBlockToast,
} from "../create-page/lib/gateway-form-requirements";
import {
  type McpServer,
  type McpServerCreateRequest,
  mcpServerCreateSchema,
} from "../types";

/**
 * View-facing shape shared by the create and edit controllers, so a single
 * presentational form can render either mode.
 */
export type GatewayFormController = {
  form: UseFormReturn<McpServerCreateRequest>;
  isEditing: boolean;
  isPending: boolean;
  organizationId: string;
  projectId: string;
  formState: GatewayFormState;
  selectedApps: string[];
  handlers: {
    onSubmit: (values: McpServerCreateRequest) => void;
    onInvalid: (errors: FieldErrors<McpServerCreateRequest>) => void;
  };
};

function useGatewayFormBase(gateway?: McpServer, initialTools: string[] = []) {
  const { organizationId, projectId } = useOrgProjectRouteContext();
  const navigate = useNavigate();
  const posthog = usePostHog();
  const toolSearch = useSuspenseCapability("tool_search");
  const isEditing = Boolean(gateway);

  const toolSearchEnabled = toolSearch?.enabled ?? false;

  const defaultValues = useMemo<McpServerCreateRequest>(() => {
    if (gateway) {
      return gatewayToFormValues(gateway);
    }
    // On create, default Personalized Tool Search to on wherever the org has the
    // capability; when it's unavailable there's nothing to consider, so off.
    return {
      ...GATEWAY_DEFAULTS,
      tool_filter: {
        allowed_tools: [...initialTools],
        discovery: { enabled: toolSearchEnabled },
      },
    };
  }, [gateway, initialTools, toolSearchEnabled]);

  const form = useForm<McpServerCreateRequest>({
    resolver: zodResolver(mcpServerCreateSchema),
    defaultValues,
  });

  // Latches synchronously on submit so a rapid second submit (Enter + click)
  // can't fire two mutations before the first flips React Query's isPending.
  const submitInFlightRef = useRef(false);

  const goToList = useCallback(() => {
    navigate({
      params: { organizationId, projectId },
      to: "/orgs/$organizationId/projects/$projectId/mcp-gateways",
    });
  }, [navigate, organizationId, projectId]);

  // Run the submit gate and, when it passes, latch in-flight and return the
  // payload to send. Returns null when the submit should be dropped (already
  // in flight, or blocked with a toast surfaced).
  const beginSubmit = useCallback(
    (values: McpServerCreateRequest): McpServerCreateRequest | null => {
      if (submitInFlightRef.current) {
        return null;
      }
      const blockToast = getSubmitBlockToast(values, { isEditing });
      if (blockToast) {
        toast.error(blockToast);
        return null;
      }
      submitInFlightRef.current = true;
      return toGatewayPayload(values, { isEditing, toolSearchEnabled });
    },
    [isEditing, toolSearchEnabled]
  );

  const endSubmit = useCallback(() => {
    submitInFlightRef.current = false;
  }, []);

  const onInvalid = useCallback(
    (errors: FieldErrors<McpServerCreateRequest>) => {
      // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
      const fieldKeys = Object.keys(
        INVALID_FIELD_LABELS
      ) as (keyof McpServerCreateRequest)[];
      const firstInvalid = fieldKeys.find((key) => errors[key]);

      if (!firstInvalid) {
        toast.error("Some required information is missing or incorrect.");
        return;
      }

      toast.error(`Check the ${INVALID_FIELD_LABELS[firstInvalid]} field.`);
      try {
        form.setFocus(firstInvalid);
      } catch {
        // Not every field maps to a focusable input (e.g. custom controls).
      }
    },
    [form]
  );

  const watchedValues = useWatch({ control: form.control });
  const formState = useMemo(
    () =>
      getGatewayFormState(
        {
          name: watchedValues.name ?? "",
          auth_type: watchedValues.auth_type,
          user_source_id: watchedValues.user_source_id,
          instructions: watchedValues.instructions ?? "",
          tool_filter: watchedValues.tool_filter,
        },
        { isEditing }
      ),
    [watchedValues, isEditing]
  );
  const selectedApps = useMemo(
    () => getSelectedAppNames(watchedValues.tool_filter?.allowed_tools),
    [watchedValues.tool_filter?.allowed_tools]
  );

  return {
    form,
    isEditing,
    organizationId,
    projectId,
    navigate,
    posthog,
    formState,
    selectedApps,
    beginSubmit,
    endSubmit,
    goToList,
    onInvalid,
  };
}

export function useGatewayCreateForm({
  initialTools = [],
}: {
  initialTools?: string[];
}): GatewayFormController {
  const base = useGatewayFormBase(undefined, initialTools);
  const { navigate, posthog, organizationId, projectId } = base;
  const { mutate: createGateway, isPending } = useCreateMcpGateway();

  const startTrackedRef = useRef(false);
  useEffect(() => {
    if (startTrackedRef.current || !posthog) {
      return;
    }
    startTrackedRef.current = true;
    posthog.capture("MCP gateway create started");
  }, [posthog]);

  const onSubmit = useCallback(
    (values: McpServerCreateRequest) => {
      if (isPending) {
        return;
      }
      const payload = base.beginSubmit(values);
      if (!payload) {
        return;
      }
      createGateway(payload, {
        onSettled: base.endSubmit,
        onSuccess: (data) => {
          posthog?.capture("MCP gateway created", {
            auth_type: values.auth_type,
            tool_count: values.tool_filter?.allowed_tools?.length ?? 0,
            tool_search_enabled:
              values.tool_filter?.discovery?.enabled ?? false,
          });
          navigate({
            params: { organizationId, projectId },
            search: { connect: data.id },
            to: "/orgs/$organizationId/projects/$projectId/mcp-gateways",
          });
        },
      });
    },
    [
      isPending,
      base.beginSubmit,
      base.endSubmit,
      createGateway,
      posthog,
      navigate,
      organizationId,
      projectId,
    ]
  );

  return {
    form: base.form,
    isEditing: false,
    isPending,
    organizationId,
    projectId,
    formState: base.formState,
    selectedApps: base.selectedApps,
    handlers: { onSubmit, onInvalid: base.onInvalid },
  };
}

export function useGatewayEditForm({
  gateway,
}: {
  gateway: McpServer;
}): GatewayFormController {
  const base = useGatewayFormBase(gateway);
  const { mutate: updateGateway, isPending } = useUpdateMcpGateway(
    gateway.id,
    gateway.binding?.type ?? "project"
  );

  const onSubmit = useCallback(
    (values: McpServerCreateRequest) => {
      if (isPending) {
        return;
      }
      const payload = base.beginSubmit(values);
      if (!payload) {
        return;
      }
      updateGateway(payload, {
        onSettled: base.endSubmit,
        onSuccess: base.goToList,
      });
    },
    [isPending, base.beginSubmit, base.endSubmit, base.goToList, updateGateway]
  );

  return {
    form: base.form,
    isEditing: true,
    isPending,
    organizationId: base.organizationId,
    projectId: base.projectId,
    formState: base.formState,
    selectedApps: base.selectedApps,
    handlers: { onSubmit, onInvalid: base.onInvalid },
  };
}
